/**
 * Client-side helper for talking to the chat proxy (Netlify Function at
 * /api/chat). The function holds the real provider API key server-side; the
 * browser only ever sends a lightweight shared token so the endpoint isn't
 * wide open. This is demo-grade gating — see netlify/functions/chat.ts.
 */

import { ChatMessage } from "./session";
import { POC_TOKEN } from "./poc-token.generated";

const CHAT_ENDPOINT = "/api/chat";

// The public (bundled) gate value lives in ./poc-token.generated.ts, which
// app/build.sh writes from the BUN_PUBLIC_POC_TOKEN env var before bundling.
// Not a real secret — it only deters casual abuse of the demo endpoint. We use
// a generated module rather than Bun's `--env='BUN_PUBLIC_*'` glob inlining
// because that silently produced an empty string on Netlify's Bun 1.3.12 (it
// worked on Bun 1.4.x locally). A literal in a module behaves identically on
// every Bun version.

export interface SendChatOptions {
	systemPrompt: string;
	messages: ChatMessage[];
	signal?: AbortSignal;
}

/** Convert internal messages into the provider-agnostic wire format. */
function toWireMessages(messages: ChatMessage[]) {
	return messages.map((m) => ({
		role: m.role === "client" ? ("assistant" as const) : ("user" as const),
		content: m.text,
	}));
}

export interface ChatResult {
	ok: boolean;
	reply?: string;
	error?: string;
}

/** Send the conversation to the proxy and return the client's next line. */
export async function sendChat({
	systemPrompt,
	messages,
	signal,
}: SendChatOptions): Promise<ChatResult> {
	try {
		const res = await fetch(CHAT_ENDPOINT, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...(POC_TOKEN ? { "X-POC-Token": POC_TOKEN } : {}),
			},
			body: JSON.stringify({ systemPrompt, messages: toWireMessages(messages) }),
			signal,
		});

		if (!res.ok) {
			let detail = "";
			try {
				const j = await res.json();
				detail = j?.error || "";
			} catch {
				/* ignore */
			}
			return { ok: false, error: detail || `Request failed (${res.status})` };
		}

		const data = (await res.json()) as { reply?: string };
		if (!data.reply) return { ok: false, error: "Empty reply" };
		return { ok: true, reply: data.reply };
	} catch (err) {
		if ((err as Error).name === "AbortError") return { ok: false, error: "cancelled" };
		return { ok: false, error: (err as Error).message };
	}
}
